"use client";

import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useLayoutEffect, useState, ReactNode, useRef } from "react";

/** Memes donnees ? Meme reference, ou meme contenu (le serveur renvoie de nouveaux objets). */
const sameData = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

// useLayoutEffect ne fait rien (et avertit) cote serveur.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Mobile-only sticky reserve bar. Defer its JS chunk on desktop where
// `block lg:hidden` hides it — there's no reason to evaluate the price
// computation + ModalReserveMobile imports on desktop where the sticky
// sidebar already handles the booking.
const MobileFooterSticky = dynamic(
  () => import("./(components)/MobileFooterSticky"),
  {
    ssr: false,
    loading: () => null,
  }
);

// Newsletter widget — far below the fold, defer JS to keep TBT low.
// Reuse the homepage's redesigned SectionNewsletter ("CARNET DE VOYAGE —
// Recevez nos pépites locales…") so the tour page matches the rest of the
// site's brand presentation (same component as city/country pages).
const SectionNewsletter = dynamic(
  () =>
    import(
      "@/app/[lang]/[currency]/(chrome)/(home)/desktop/SectionNewsletter"
    ),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 620 }} aria-hidden="true" />,
  }
);
// Burgundy "VOUS HÉSITEZ ?" live-chat CTA — rendered outside the page's
// `.container` wrapper so the band can run edge-to-edge on mobile.
const CityAdvisorCTA = dynamic(
  () =>
    import(
      "@/app/[lang]/[currency]/(chrome)/city/[[...slug]]/CityAdvisorCTA"
    ),
  { ssr: false, loading: () => <div style={{ minHeight: 220 }} aria-hidden="true" /> }
);
import { Route } from "next";
import { useAppSelector, useAppDispatch, useAppStore } from "@/app/lib/hooks";
import { resetBooking } from "@/app/lib/reducers/bookingReducer";
import {
  getCityData,
  setCityData,
} from "@/app/lib/reducers/citiesReducer";
import {
  getTourDetails,
  getTourGallery,
  setTourDetails,
  setGalleries,
} from "@/app/lib/reducers/tourReducer";
import { CityData, Gallery, TourDetails } from "@/data/types";
import { ListingGalleryImage } from "@/components/listing-image-gallery/utils/types";
import { perPersonPriceFor } from "@/utils/pricingUtils";

const ListingImageGallery = dynamic(
  () => import("@/components/listing-image-gallery/ListingImageGallery"),
  { ssr: false, loading: () => null }
);

interface Props {
  children: ReactNode;
  tourSlug: string;
  citySlug: string;
  initialTour: TourDetails | null;
  initialCity: CityData | null;
  initialGalleries: Gallery[];
}

const TourLayoutClient = ({
  children,
  tourSlug,
  citySlug,
  initialTour,
  initialCity,
  initialGalleries,
}: Props) => {
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector(
    (state) => state.language.selectedLanguage
  );
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const modal = searchParams?.get("modal");

  const tour = useAppSelector((state) => state.tour.tourDetails);
  const galleries: Gallery[] = useAppSelector((state) => state.tour.galleries);
  const city = useAppSelector((state) => state.cities.currentCityData);

  const hasPushedDataLayer = useRef(false);

  // Synchronously hydrate Redux store from server data BEFORE children render.
  // useState initializer runs once per mount, synchronously.
  //
  // Seulement si le store n'a pas DEJA ces donnees : pendant une navigation
  // (changement de devise), React re-tente le rendu de la nouvelle page des
  // dizaines de fois avant de l'afficher ; chaque tentative re-ecrivait le meme
  // tour dans le store et redessinait la page encore a l'ecran (CTO, 2026-10-06).
  const store = useAppStore();
  useState(() => {
    const current = store.getState() as any;
    if (initialTour && !sameData(current?.tour?.tourDetails, initialTour)) dispatch(setTourDetails(initialTour));
    // Pas de tour fourni par le serveur (tour reserve a un client, GT-261007-HVGQ, ou
    // backend indisponible) : on ne garde pas celui d'une fiche visitee avant.
    if (!initialTour && current?.tour?.tourDetails) dispatch(setTourDetails(null as any));
    if (initialCity && !sameData(current?.cities?.currentCityData, initialCity)) dispatch(setCityData(initialCity));
    if (initialGalleries && initialGalleries.length > 0 && !sameData(current?.tour?.galleries, initialGalleries)) {
      dispatch(setGalleries(initialGalleries));
    }
  });

  // Re-hydrate when navigating between tours (server provides new initial
  // data). Kept in the render pass: the server render needs the store filled
  // to output the tour (SEO).
  const lastSlugRef = useRef(tourSlug);
  if (lastSlugRef.current !== tourSlug) {
    if (initialTour) dispatch(setTourDetails(initialTour));
    else dispatch(setTourDetails(null as any));
    if (initialCity) dispatch(setCityData(initialCity));
    if (initialGalleries) dispatch(setGalleries(initialGalleries));
    lastSlugRef.current = tourSlug;
  }

  // Remise a zero de la reservation (au montage et a chaque changement de
  // tour) dans un effet de LAYOUT, plus pendant le rendu (CTO, 2026-10-06).
  //   - Un effet de layout passe avant les effets passifs de TOUS les enfants :
  //     les selecteurs (duree / voyageurs / date) amorcent donc toujours leurs
  //     valeurs APRES la remise a zero — la raison qui l'avait fait mettre dans
  //     le rendu (sinon : panneau bloque a « 0,00 € »).
  //   - Dans le rendu, elle partait aussi pendant les rendus que React jette :
  //     en changeant de devise, la nouvelle page (pas encore affichee) videait la
  //     reservation, l'ancienne page encore a l'ecran la re-remplissait, React
  //     re-tentait la nouvelle… ~45 aller-retours en 2 s, l'affichage sautait.
  useIsomorphicLayoutEffect(() => {
    dispatch(resetBooking());
  }, [dispatch, tourSlug]);

  // Fallback client-side fetch:
  //  (a) when server data is missing (failed fetch on Node), or
  //  (b) when language changes after the initial render (dropdown w/o navigation).
  // The URL language param drives the initial server fetch; this covers the
  // edge case where Redux selectedLanguage diverges from the URL.
  const initialLangRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tourSlug || !selectedLanguage) return;

    if (initialLangRef.current === null) {
      initialLangRef.current = selectedLanguage;
    }

    const langChanged = selectedLanguage !== initialLangRef.current;
    const needsTour = !initialTour || langChanged;
    const needsCity = !initialCity || langChanged;
    const needsGallery =
      !initialGalleries || initialGalleries.length === 0 || langChanged;

    if (needsCity) {
      dispatch(getCityData(citySlug, selectedLanguage));
    }
    if (needsTour) {
      dispatch(getTourDetails(tourSlug, selectedLanguage));
    }
    if (needsGallery) {
      dispatch(getTourGallery(tourSlug));
    }

    if (langChanged) {
      initialLangRef.current = selectedLanguage;
    }
  }, [tourSlug, citySlug, dispatch, selectedLanguage, initialTour, initialCity, initialGalleries]);

  useEffect(() => {
    if (
      !hasPushedDataLayer.current &&
      tour?.uuid &&
      selectedLanguage &&
      city?.entry?.title
    ) {
      const firstActiveDuration = tour.durations?.find((d) => d.activated);
      const firstActiveSchedule = firstActiveDuration?.schedules?.find(
        (s) => s.activated
      );
      const firstActivePricing = firstActiveSchedule?.pricing?.find(
        (p) => p.activated
      );

      // Tarif par personne indicatif : une ligne "Group" porte le total du
      // groupe, on le ramene a son plus petit effectif desservi.
      const price = firstActivePricing
        ? perPersonPriceFor(firstActivePricing, firstActivePricing.minPeople || 1)
        : null;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "tour_view",
        tour_id: tour.uuid,
        page_type: "product",
        price: price,
        language: selectedLanguage,
        city: city.entry.title.toLowerCase(),
      });

      hasPushedDataLayer.current = true;
    }
  }, [tour, city, selectedLanguage]);

  const handleCloseModalImageGallery = () => {
    let params = new URLSearchParams(document.location.search);
    params.delete("modal");
    router.push(`${pathname}/?${params.toString()}` as Route);
  };

  const getImageGalleryListing = (): ListingGalleryImage[] => {
    return galleries.map((gallery, index) => ({
      id: index,
      url: gallery.path,
    }));
  };

  return (
    <div className="ListingDetailPage mt-2">
      <div className="container ListingDetailPage__content">{children}</div>
      <ListingImageGallery
        isShowModal={modal === "PHOTO_TOUR_SCROLLABLE"}
        onClose={handleCloseModalImageGallery}
        images={getImageGalleryListing()}
      />
      {/* CityAdvisorCTA is rendered OUTSIDE the `.container` wrapper so
          the burgundy band runs edge-to-edge of the viewport on mobile. */}
      <CityAdvisorCTA />
      {/* Newsletter footer — hidden on mobile for the tour-detail page so
          the booking CTA + sticky reserve bar stay the primary action.
          On md+ the full "Carnet de voyage" newsletter section renders. */}
      <div className="hidden md:block">
        <SectionNewsletter />
      </div>
      {/* Seulement quand le tour de la page est charge : pas de « Réserver » sur un tour
          reserve a un autre client (GT-261007-HVGQ). */}
      {(initialTour || tour) && <MobileFooterSticky />}
    </div>
  );
};

export default TourLayoutClient;
